import {
  ColDef,
  GetRowIdParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IRowNode,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { switchMap, tap } from 'rxjs';

import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';

import { ApmTranslatePipe, ToastService } from '@mitsubishi/core';
import { ExpiringMarkup, PriceListService } from '@mitsubishi/pricing-list/data-access';
import { ButtonComponent, SearchBoxComponent } from '@mitsubishi/shared/ui';
import { TableComponent } from '@mitsubishi/ui/table';

import { EXPIRING_MARKUP_RESOURCE_IDS } from '../../expiring-markup.constants';
import { getPriceListGroupAutoColumnDef, getPriceListGroupColumns } from '../../price-list-subfamilies-columns';

@Component({
  selector: 'apm-bulk-publish-markup-dialog',
  templateUrl: './bulk-publish-markup-dialog.component.html',
  styleUrl: './bulk-publish-markup-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ApmTranslatePipe, ButtonComponent, SearchBoxComponent, TableComponent],
})
export class BulkPublishMarkupDialogComponent implements OnInit {
  readonly #priceListService = inject(PriceListService);
  readonly #dialogRef = inject(MatDialogRef<BulkPublishMarkupDialogComponent, boolean>);
  readonly #toastService = inject(ToastService);

  protected readonly resourceIds = EXPIRING_MARKUP_RESOURCE_IDS;
  protected readonly rows = signal<ExpiringMarkup[]>([]);
  protected readonly selectedLeafCount = signal(0);
  protected readonly publishedAny = signal(false);
  readonly #searchText = signal('');

  protected readonly columnDefs: ColDef[] = getPriceListGroupColumns();
  protected readonly autoGroupColumnDef: ColDef = (() => {
    const base = getPriceListGroupAutoColumnDef();
    return {
      ...base,
      headerName: '',
      headerCheckboxSelection: true,
      cellRendererParams: {
        ...base.cellRendererParams,
        checkbox: true,
      },
    };
  })();
  protected readonly customGridOptions: GridOptions = {
    groupSelectsChildren: true,
    suppressRowClickSelection: true,
  };
  protected readonly getRowId = (params: GetRowIdParams) => (params.data as ExpiringMarkup).markupId;

  #gridApi?: GridApi;

  ngOnInit(): void {
    this.#loadDrafts();
  }

  protected onGridReady(event: GridReadyEvent): void {
    this.#gridApi = event.api;
    event.api.updateGridOptions({
      isExternalFilterPresent: () => this.#searchText().length > 0,
      doesExternalFilterPass: (node: IRowNode<ExpiringMarkup>) =>
        node.data?.subfamilyCode?.toLowerCase().includes(this.#searchText()) ?? false,
    });
  }

  protected onSearchChanged(value: string | null): void {
    this.#searchText.set((value ?? '').toLowerCase());
    this.#gridApi?.onFilterChanged();
  }

  protected onSelectionChanged(event: SelectionChangedEvent): void {
    const leafCount = event.api.getSelectedNodes().filter(node => !node.group).length;
    this.selectedLeafCount.set(leafCount);
  }

  protected publish(): void {
    const ids = (this.#gridApi?.getSelectedNodes() ?? [])
      .filter(node => !node.group)
      .map(node => (node.data as ExpiringMarkup).markupId);
    if (!ids.length) return;

    this.#priceListService
      .bulkFinalizeMarkup(ids)
      .pipe(
        tap(() => this.#toastService.success({ resourceId: this.resourceIds.publishSuccess })),
        switchMap(() => this.#priceListService.getDraftMarkups())
      )
      .subscribe(list => {
        this.publishedAny.set(true);
        if (list.length === 0) {
          this.#dialogRef.close(true);
          return;
        }
        this.rows.set(list);
        this.selectedLeafCount.set(0);
        this.#gridApi?.deselectAll();
      });
  }

  protected cancel(): void {
    this.#dialogRef.close(this.publishedAny());
  }

  #loadDrafts(): void {
    this.#priceListService.getDraftMarkups().subscribe(list => {
      this.rows.set(list);
      this.selectedLeafCount.set(0);
      this.#gridApi?.deselectAll();
    });
  }
}
